import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import RecalculationProgressModal from "../components/RecalculationProgressModal";
import root from "../assets/styles/Root.module.css";
import s from "../assets/styles/Admin.module.css";

export default function MultiAccountPage() {
   const { authFetch } = useAuth();
   const [users, setUsers] = useState([]);
   const [loading, setLoading] = useState(true);
   const [searchQuery, setSearchQuery] = useState("");
   const [riskLevelFilter, setRiskLevelFilter] = useState("");
   const [recalculating, setRecalculating] = useState(false);
   const [recalculatingUser, setRecalculatingUser] = useState(null);
   const [message, setMessage] = useState(null);
   const [showProgressModal, setShowProgressModal] = useState(false);
   const [progressModalType, setProgressModalType] = useState("all"); // "all" or "single"
   const [progressUserName, setProgressUserName] = useState(null);
   const [showDetailsModal, setShowDetailsModal] = useState(false);
   const [selectedUserDetails, setSelectedUserDetails] = useState(null);
   const [userEvents, setUserEvents] = useState([]);
   const [eventsLoading, setEventsLoading] = useState(false);
   const [eventTypeFilter, setEventTypeFilter] = useState("");
   const [riskFilter, setRiskFilter] = useState("");

   useEffect(() => {
      let mounted = true;
      (async () => {
         try {
            let url = "/api/multiaccount/users/";
            const params = new URLSearchParams();
            if (searchQuery) params.append("search", searchQuery);
            if (riskLevelFilter) params.append("risk_level", riskLevelFilter);
            if (params.toString()) url += "?" + params.toString();

            const res = await authFetch(url);
            const data = await res.json();
            if (mounted) {
               setUsers(data);
               setLoading(false);
            }
         } catch (error) {
            console.error("Error loading users:", error);
            if (mounted) setLoading(false);
         }
      })();
      return () => { mounted = false; };
   }, [authFetch, searchQuery, riskLevelFilter]);

   const handleRecalculateAll = async () => {
      if (!window.confirm("Пересчитать риски для всех пользователей? Это может занять некоторое время.")) {
         return;
      }

      setRecalculating(true);
      setMessage(null);
      setProgressModalType("all");
      setShowProgressModal(true);

      try {
         // Имитируем задержку для анимации (минимум 8 секунд для всех этапов)
         const startTime = Date.now();
         const minDuration = 8000;

         const res = await authFetch("/api/multiaccount/recalculate-all/", {
            method: "POST",
         });
         const data = await res.json();

         // Ждем минимум minDuration для показа всех этапов
         const elapsed = Date.now() - startTime;
         if (elapsed < minDuration) {
            await new Promise(resolve => setTimeout(resolve, minDuration - elapsed));
         }

         if (data.success) {
            setMessage({ type: "success", text: data.message });
            // Перезагружаем список с деталями
            const res2 = await authFetch("/api/multiaccount/users/");
            const data2 = await res2.json();
            setUsers(data2);
         } else {
            setMessage({ type: "error", text: data.error || "Ошибка при пересчете" });
         }
      } catch (error) {
         setMessage({ type: "error", text: "Ошибка при пересчете рисков" });
      } finally {
         setRecalculating(false);
         // Закрываем модальное окно через небольшую задержку
         setTimeout(() => {
            setShowProgressModal(false);
         }, 500);
      }
   };

   const handleRecalculateUser = async (userId) => {
      const user = users.find(u => u.id === userId);
      const userName = user ? (user.email || user.username) : `ID ${userId}`;

      setRecalculatingUser(userId);
      setProgressModalType("single");
      setProgressUserName(userName);
      setShowProgressModal(true);
      setMessage(null);

      try {
         // Имитируем задержку для анимации (минимум 8 секунд для всех этапов)
         const startTime = Date.now();
         const minDuration = 8000;

         const res = await authFetch(`/api/multiaccount/users/${userId}/risk/recalculate/`, {
            method: "POST",
         });
         const data = await res.json();

         // Ждем минимум minDuration для показа всех этапов
         const elapsed = Date.now() - startTime;
         if (elapsed < minDuration) {
            await new Promise(resolve => setTimeout(resolve, minDuration - elapsed));
         }

         if (data.success) {
            // Загружаем обновленные данные пользователя с деталями
            const resDetails = await authFetch(`/api/multiaccount/users/${userId}/risk/`);
            const userDetails = await resDetails.json();

            // Обновляем пользователя в списке
            setUsers(users.map(u =>
               u.id === userId
                  ? { ...u, risk_score: data.risk_score, risk_level: data.risk_level, risk_score_details: userDetails.risk_score_details }
                  : u
            ));
            setMessage({ type: "success", text: `Риск для пользователя ${userName} пересчитан` });
         } else {
            setMessage({ type: "error", text: data.error || "Ошибка при пересчете" });
         }
      } catch (error) {
         setMessage({ type: "error", text: "Ошибка при пересчете риска" });
      } finally {
         setRecalculatingUser(null);
         // Закрываем модальное окно через небольшую задержку
         setTimeout(() => {
            setShowProgressModal(false);
         }, 500);
      }
   };

   const getRiskLevelColor = (level) => {
      const colors = {
         green: "#28a745",
         yellow: "#ffc107",
         orange: "#fd7e14",
         red: "#dc3545",
      };
      return colors[level] || "#6c757d";
   };

   const getRiskLevelName = (level) => {
      const names = {
         green: "Зеленый",
         yellow: "Желтый",
         orange: "Оранжевый",
         red: "Красный",
      };
      return names[level] || level;
   };

   const handleShowDetails = async (user) => {
      setShowDetailsModal(true);
      setEventsLoading(true);
      setUserEvents([]);
      setEventTypeFilter("");
      setRiskFilter("");

      // Загружаем детали риска
      let userDetails = user;
      if (!user.risk_score_details) {
         try {
            const res = await authFetch(`/api/multiaccount/users/${user.id}/risk/?recalculate=false`);
            const data = await res.json();
            userDetails = { ...user, risk_score_details: data.risk_score_details };
         } catch (error) {
            console.error("Error loading details:", error);
         }
      }
      setSelectedUserDetails(userDetails);

      // Загружаем события пользователя
      try {
         const eventsRes = await authFetch(`/api/multiaccount/users/${user.id}/events/`);
         const eventsData = await eventsRes.json();
         setUserEvents(eventsData);
      } catch (error) {
         console.error("Error loading events:", error);
         setUserEvents([]);
      } finally {
         setEventsLoading(false);
      }
   };

   // Фильтрация событий
   const filteredEvents = userEvents.filter(event => {
      // Фильтр по типу события
      if (eventTypeFilter && event.event_type !== eventTypeFilter) {
         return false;
      }

      // Фильтр по риску
      if (riskFilter === "suspicious" && !event.is_suspicious) {
         return false;
      }
      if (riskFilter === "normal" && event.is_suspicious) {
         return false;
      }

      return true;
   });

   // Получаем уникальные типы событий для фильтра
   const eventTypes = [...new Set(userEvents.map(e => e.event_type))];

   const getEventTypeName = (type) => {
      const names = {
         register: 'Регистрация',
         login: 'Вход',
         logout: 'Выход',
         deposit: 'Депозит',
         withdraw: 'Вывод',
         invite_sent: 'Отправлено приглашение',
         invite_accepted: 'Принято приглашение',
         play_game: 'Игра',
         open_case: 'Открытие кейса',
      };
      return names[type] || type;
   };

   return (
      <div className={s.page}>
         <div className={s.header}>
            <h2 className={s.title}>Мультиаккаунтинг</h2>
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
               <input
                  type="text"
                  placeholder="Поиск по email или username..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={s.searchInput}
                  style={{ width: "250px" }}
               />
               <select
                  value={riskLevelFilter}
                  onChange={(e) => setRiskLevelFilter(e.target.value)}
                  className={s.searchInput}
                  style={{ width: "150px" }}
               >
                  <option value="">Все уровни</option>
                  <option value="red">Красный</option>
                  <option value="orange">Оранжевый</option>
                  <option value="yellow">Желтый</option>
                  <option value="green">Зеленый</option>
               </select>
               <button
                  className={root.btnPrimary}
                  onClick={handleRecalculateAll}
                  disabled={recalculating}
               >
                  {recalculating ? "Пересчет..." : "Пересчитать всех"}
               </button>
            </div>
         </div>

         {message && (
            <div style={{
               padding: "10px",
               marginBottom: "20px",
               backgroundColor: message.type === "success" ? "#d4edda" : "#f8d7da",
               color: message.type === "success" ? "#155724" : "#721c24",
               borderRadius: "4px",
               border: `1px solid ${message.type === "success" ? "#c3e6cb" : "#f5c6cb"}`
            }}>
               {message.text}
            </div>
         )}

         <div className={`${s.card} ${s.tableWrap}`}>
            {loading ? (
               <div>Загрузка...</div>
            ) : users.length === 0 ? (
               <div>Пользователи не найдены</div>
            ) : (
               <table className={s.table}>
                  <thead>
                     <tr>
                        <th>ID</th>
                        <th>Email</th>
                        <th>Username</th>
                        <th>Risk Score</th>
                        <th>Уровень риска</th>
                        <th>Связей</th>
                        <th>Последний расчет</th>
                        <th>Действия</th>
                     </tr>
                  </thead>
                  <tbody>
                     {users.map(user => (
                        <tr key={user.id}>
                           <td>{user.id}</td>
                           <td>
                              <Link
                                 to={`/users/${user.id}`}
                                 style={{ color: '#007bff', textDecoration: 'none' }}
                              >
                                 {user.email}
                              </Link>
                           </td>
                           <td>{user.username}</td>
                           <td>
                              <strong style={{ fontSize: "16px" }}>
                                 {user.risk_score.toFixed(2)}
                              </strong>
                           </td>
                           <td>
                              <span style={{
                                 color: getRiskLevelColor(user.risk_level),
                                 fontWeight: "bold",
                                 padding: "4px 8px",
                                 borderRadius: "4px",
                                 backgroundColor: getRiskLevelColor(user.risk_level) + "20"
                              }}>
                                 {getRiskLevelName(user.risk_level)}
                              </span>
                           </td>
                           <td>{user.links_count || 0}</td>
                           <td>
                              {user.last_risk_calculated_at
                                 ? new Date(user.last_risk_calculated_at).toLocaleString("ru-RU")
                                 : "—"
                              }
                           </td>
                           <td>
                              <div style={{ display: "flex", gap: "5px" }}>
                                 <button
                                    className={root.btn}
                                    onClick={() => handleShowDetails(user)}
                                    style={{ fontSize: "12px", padding: "4px 8px" }}
                                 >
                                    Детали
                                 </button>
                                 <button
                                    className={root.btn}
                                    onClick={() => handleRecalculateUser(user.id)}
                                    disabled={recalculatingUser === user.id}
                                    style={{ fontSize: "12px", padding: "4px 8px" }}
                                 >
                                    {recalculatingUser === user.id ? "..." : "Пересчитать"}
                                 </button>
                              </div>
                           </td>
                        </tr>
                     ))}
                  </tbody>
               </table>
            )}
         </div>

         <RecalculationProgressModal
            open={showProgressModal}
            onClose={() => setShowProgressModal(false)}
            isAllUsers={progressModalType === "all"}
            userName={progressUserName}
            canClose={!recalculating && !recalculatingUser}
         />

         {/* Модальное окно с детальной информацией о начислении очков */}
         {showDetailsModal && selectedUserDetails && (
            <div style={{
               position: "fixed",
               top: 0,
               left: 0,
               right: 0,
               bottom: 0,
               backgroundColor: "rgba(0, 0, 0, 0.5)",
               display: "flex",
               alignItems: "center",
               justifyContent: "center",
               zIndex: 1000,
            }} onClick={() => setShowDetailsModal(false)}>
               <div style={{
                  backgroundColor: "white",
                  borderRadius: "8px",
                  padding: "24px",
                  maxWidth: "1200px",
                  maxHeight: "90vh",
                  overflow: "auto",
                  width: "95%",
               }} onClick={(e) => e.stopPropagation()}>
                  <div style={{
                     display: "flex",
                     justifyContent: "space-between",
                     alignItems: "center",
                     marginBottom: "20px",
                  }}>
                     <h3 style={{ margin: 0 }}>
                        Детали начисления очков риска: {selectedUserDetails.email || selectedUserDetails.username}
                     </h3>
                     <button
                        onClick={() => setShowDetailsModal(false)}
                        style={{
                           background: "none",
                           border: "none",
                           fontSize: "24px",
                           cursor: "pointer",
                           color: "#666",
                        }}
                     >
                        ×
                     </button>
                  </div>

                  {selectedUserDetails.risk_score_details ? (
                     <div>
                        <div style={{
                           marginBottom: "20px",
                           padding: "15px",
                           backgroundColor: "#f8f9fa",
                           borderRadius: "6px",
                        }}>
                           <strong>Общий риск: </strong>
                           <span style={{
                              fontSize: "24px",
                              fontWeight: "bold",
                              color: getRiskLevelColor(selectedUserDetails.risk_level),
                           }}>
                              {selectedUserDetails.risk_score_details.total_score?.toFixed(2) || selectedUserDetails.risk_score.toFixed(2)}
                           </span>
                           {selectedUserDetails.risk_score_details.calculated_at && (
                              <div style={{ marginTop: "5px", fontSize: "12px", color: "#666" }}>
                                 Рассчитано: {new Date(selectedUserDetails.risk_score_details.calculated_at).toLocaleString("ru-RU")}
                              </div>
                           )}
                        </div>

                        <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
                           {selectedUserDetails.risk_score_details.factors?.map((factor, index) => (
                              <div key={index} style={{
                                 border: "1px solid #dee2e6",
                                 borderRadius: "6px",
                                 padding: "15px",
                                 backgroundColor: factor.points > 0 ? "#fff3cd" : "#f8f9fa",
                              }}>
                                 <div style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: "10px",
                                 }}>
                                    <div>
                                       <strong style={{ fontSize: "16px" }}>{factor.name}</strong>
                                       <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
                                          {factor.description}
                                       </div>
                                    </div>
                                    <div style={{ textAlign: "right" }}>
                                       <div style={{
                                          fontSize: "18px",
                                          fontWeight: "bold",
                                          color: factor.points > 0 ? "#dc3545" : "#6c757d",
                                       }}>
                                          {factor.points.toFixed(2)} / {factor.max_points.toFixed(2)}
                                       </div>
                                       {factor.count > 0 && (
                                          <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
                                             Найдено: {factor.count}
                                          </div>
                                       )}
                                    </div>
                                 </div>

                                 {factor.details && factor.details.length > 0 && (
                                    <div style={{
                                       marginTop: "10px",
                                       padding: "10px",
                                       backgroundColor: "white",
                                       borderRadius: "4px",
                                       border: "1px solid #dee2e6",
                                    }}>
                                       <strong style={{ fontSize: "12px", color: "#666" }}>Детали:</strong>
                                       <div style={{ marginTop: "8px", display: "flex", flexDirection: "column", gap: "8px" }}>
                                          {factor.details.map((detail, detailIndex) => (
                                             <div key={detailIndex} style={{
                                                padding: "8px",
                                                backgroundColor: "#f8f9fa",
                                                borderRadius: "4px",
                                                fontSize: "13px",
                                             }}>
                                                {detail.username && (
                                                   <div>
                                                      <strong>Пользователь: </strong>
                                                      <Link
                                                         to={`/users/${detail.user_id}`}
                                                         style={{ color: '#007bff', textDecoration: 'none' }}
                                                      >
                                                         {detail.username} ({detail.email})
                                                      </Link>
                                                   </div>
                                                )}
                                                {detail.device_count && (
                                                   <div>
                                                      <strong>Совпадений устройств: </strong>{detail.device_count}
                                                   </div>
                                                )}
                                                {detail.evidence && (
                                                   <div style={{ marginTop: "4px", fontSize: "11px", color: "#666" }}>
                                                      {JSON.stringify(detail.evidence, null, 2)}
                                                   </div>
                                                )}
                                                {detail.first_detected_at && (
                                                   <div style={{ marginTop: "4px", fontSize: "11px", color: "#666" }}>
                                                      Обнаружено: {new Date(detail.first_detected_at).toLocaleString("ru-RU")}
                                                   </div>
                                                )}
                                             </div>
                                          ))}
                                       </div>
                                    </div>
                                 )}
                              </div>
                           ))}
                        </div>
                     </div>
                  ) : (
                     <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
                        Детальная информация недоступна. Попробуйте пересчитать риск для этого пользователя.
                     </div>
                  )}

                  {/* Логи действий пользователя */}
                  <div style={{ marginTop: "30px", borderTop: "2px solid #dee2e6", paddingTop: "20px" }}>
                     <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
                        <h4 style={{ margin: 0 }}>Логи действий пользователя</h4>
                        {!eventsLoading && userEvents.length > 0 && (
                           <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                              <select
                                 value={eventTypeFilter}
                                 onChange={(e) => setEventTypeFilter(e.target.value)}
                                 style={{
                                    padding: "6px 10px",
                                    borderRadius: "4px",
                                    border: "1px solid #dee2e6",
                                    fontSize: "13px",
                                    cursor: "pointer",
                                 }}
                              >
                                 <option value="">Все действия</option>
                                 {eventTypes.map(type => (
                                    <option key={type} value={type}>{getEventTypeName(type)}</option>
                                 ))}
                              </select>
                              <select
                                 value={riskFilter}
                                 onChange={(e) => setRiskFilter(e.target.value)}
                                 style={{
                                    padding: "6px 10px",
                                    borderRadius: "4px",
                                    border: "1px solid #dee2e6",
                                    fontSize: "13px",
                                    cursor: "pointer",
                                 }}
                              >
                                 <option value="">Все по риску</option>
                                 <option value="suspicious">Подозрительные</option>
                                 <option value="normal">Обычные</option>
                              </select>
                              {filteredEvents.length !== userEvents.length && (
                                 <span style={{ fontSize: "12px", color: "#666" }}>
                                    Показано: {filteredEvents.length} из {userEvents.length}
                                 </span>
                              )}
                           </div>
                        )}
                     </div>

                     {eventsLoading ? (
                        <div style={{ textAlign: "center", padding: "20px", color: "#666" }}>
                           Загрузка событий...
                        </div>
                     ) : userEvents.length === 0 ? (
                        <div style={{ textAlign: "center", padding: "20px", color: "#666" }}>
                           События не найдены
                        </div>
                     ) : filteredEvents.length === 0 ? (
                        <div style={{ textAlign: "center", padding: "20px", color: "#666" }}>
                           События не найдены по выбранным фильтрам
                        </div>
                     ) : (
                        <div style={{
                           maxHeight: "400px",
                           overflowY: "auto",
                           border: "1px solid #dee2e6",
                           borderRadius: "6px",
                        }}>
                           <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                              <thead style={{ backgroundColor: "#f8f9fa", position: "sticky", top: 0 }}>
                                 <tr>
                                    <th style={{ padding: "10px", textAlign: "left", borderBottom: "2px solid #dee2e6" }}>Дата/Время</th>
                                    <th style={{ padding: "10px", textAlign: "left", borderBottom: "2px solid #dee2e6" }}>Действие</th>
                                    <th style={{ padding: "10px", textAlign: "left", borderBottom: "2px solid #dee2e6" }}>Сумма</th>
                                    <th style={{ padding: "10px", textAlign: "left", borderBottom: "2px solid #dee2e6" }}>Статус</th>
                                 </tr>
                              </thead>
                              <tbody>
                                 {filteredEvents.map((event, index) => (
                                    <tr
                                       key={index}
                                       style={{
                                          backgroundColor: event.is_suspicious ? "#fff3cd" : "white",
                                          borderBottom: "1px solid #dee2e6",
                                       }}
                                    >
                                       <td style={{ padding: "10px" }}>
                                          {new Date(event.timestamp).toLocaleString("ru-RU")}
                                       </td>
                                       <td style={{ padding: "10px" }}>
                                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                             <span>{getEventTypeName(event.event_type)}</span>
                                             {event.is_suspicious && (
                                                <span style={{
                                                   backgroundColor: "#dc3545",
                                                   color: "white",
                                                   padding: "2px 6px",
                                                   borderRadius: "4px",
                                                   fontSize: "10px",
                                                   fontWeight: "bold",
                                                }}>
                                                   ⚠ ПОДОЗРИТЕЛЬНО
                                                </span>
                                             )}
                                          </div>
                                          {event.is_suspicious && event.suspicious_reasons && event.suspicious_reasons.length > 0 && (
                                             <div style={{
                                                marginTop: "4px",
                                                fontSize: "11px",
                                                color: "#856404",
                                                fontStyle: "italic",
                                             }}>
                                                {event.suspicious_reasons.map((reason, reasonIndex) => (
                                                   <div key={reasonIndex}>• {reason}</div>
                                                ))}
                                             </div>
                                          )}
                                       </td>
                                       <td style={{ padding: "10px" }}>
                                          {event.amount ? `$${parseFloat(event.amount).toFixed(2)}` : "—"}
                                       </td>
                                       <td style={{ padding: "10px" }}>
                                          {event.payload?.status && (
                                             <span style={{
                                                padding: "2px 6px",
                                                borderRadius: "4px",
                                                fontSize: "11px",
                                                backgroundColor: event.payload.status === 'confirmed' ? "#d4edda" : "#fff3cd",
                                                color: event.payload.status === 'confirmed' ? "#155724" : "#856404",
                                             }}>
                                                {event.payload.status}
                                             </span>
                                          )}
                                       </td>
                                    </tr>
                                 ))}
                              </tbody>
                           </table>
                        </div>
                     )}
                  </div>
               </div>
            </div>
         )}
      </div>
   );
}

